<template>
  <div id="root-div">
    <transition name="slide-nav" @after-leave="afterLeaveNav">
      <nav-bar
        v-show="navVisible"
        @update:selectedHistUUID="updateHistUUID"
        @closeNav="closeNav"
        :style="{ zIndex: zIndexNav }"
      />
    </transition>
    <section id="chat-window" :style="{ zIndex: zIndexChat }">
      <header id="chat-window-header">
        <img
          id="hamburger"
          @click="showNav"
          :src="hamburgerBtnPath"
          alt="menu_button"
        />
        <img id="chat-header-logo" :src="lawraLogoPath" alt="lawra_logo" />
        <h2>Law-RA</h2>
      </header>
      <article id="chat-box" ref="chatBox">
        <message-box
          v-for="(chat, index) in currentChatHist"
          :key="index"
          :isBot="chat.isBot"
          :content="chat.content"
          :caseNums="chat.caseNums"
          :isLoading="chat.isLoading ? true : false"
          @showref="showRef"
          @summarize="summarize"
        />
      </article>
      <div id="input-box">
        <textarea
          v-model="userInput"
          id="input-textarea"
          ref="input-textarea"
          rows="1"
          placeholder="질문을 입력하세요."
          :style="{ height: '${height}px' }"
          :readonly="isDisabled"
          @keydown.enter="handleEnter"
        ></textarea>
        <button
          id="input-send-button"
          :disabled="isDisabled"
          @click="isDisabled ? null : sendMessage()"
        >
          ↑
        </button>
        <div v-if="isBegin" id="query-example">
          <p @click="sendMessage('39533 판례의 주문과 판사명을 알려줘.')">
            39533 판례의 주문과 판사명을 알려줘.
          </p>
          <p @click="sendMessage('2021두39034 판례의 내용을 요약해줘.')">
            2021두39034 판례의 내용을 요약해줘.
          </p>
        </div>
      </div>
    </section>
    <transition name="slide-case-window" @after-leave="afterLeaveRef">
      <case-ref-window
        v-show="caseWindowVisible"
        :caseRef="caseRef"
        :style="{ zIndex: zIndexRef }"
        @closeWindow="closeRef"
      />
    </transition>
    <transition name="overlay">
      <div
        id="overlay"
        v-show="overlayVisible"
        :style="{ zIndex: zIndexOverlay }"
      ></div>
    </transition>
  </div>
</template>

<script>
import MessageBox from '@/components/MessageBox.vue';
import NavBar from '@/components/NavBar.vue';
import CaseRefWindow from '@/components/CaseRefWindow.vue';

export default {
  components: { MessageBox, NavBar, CaseRefWindow },
  data() {
    return {
      hamburgerBtnPath: require('@/assets/icons/hamburger.png'),
      lawraLogoPath: require('@/assets/icons/lawra_logo.png'),
      baseURL: process.env.VUE_APP_API_BASE_URL,
      chatHistUUID: '', // NavBar에서 받아올 대화내역 UUID
      userInput: '', // 사용자 입력 메시지 양방향 바인딩

      isDisabled: false, // 봇답변시 입력창 및 버튼 비활성화 관리
      navVisible: false,
      overlayVisible: false,
      caseWindowVisible: false,
      isBegin: true,

      // z-index 관리. 모바일 & 태블릿환경에서 특정요소에 집중할때만 zIndex 오버레이보다 높게 설정
      zIndexNav: 1,
      zIndexChat: 1,
      zIndexRef: 1,
      zIndexOverlay: 2,

      /** 채팅내역이 기록될 오브젝트
       * @type { uuid:[{content: string, isBot: boolean, caseNums: array[string]}, {content: ...}], uuid:[{...}, ...] }
       * @property {string} uuid -> 대화내역 uuid (a-zA-z0-9 3글자)
       * @property {string} content -> 채팅 메시지
       * @property {boolean} isBot -> 봇인지 사람인지 여부 (아이콘 구별)
       * @property {array[string]} caseNums -> 참고판례(사건번호) 목록. 봇인경우만 조건부로 입력
       * @property {key:array} temp:[] -> uuid가 없을시 임시 대화내역 저장공간 (4글자로 key 겹침 x)
       */
      allChats: { temp: [] },

      /** 참고판례 정보가 기록될 오브젝트
       * @type { 사건번호:{ 판례주제: String, 사건번호: String, 법원명: string, 선고일자: string, 판시사항: string, 판결요지: string, \
       * 참조조문: string, 참조판례: string, 주문: string, 판례전문: string, 판사명: string}, 사건번호:{ 판례주제: ... }, ...}
       */
      allCaseRefs: {},
      caseRef: {}, // 현재 참고판례 정보 바인딩
    };
  },
  computed: {
    /** 반응형변수. 현재 uuid에 해당하는 대화내역이 있는지 확인하고 없으면 빈 어레이 반환 */
    currentChatHist() {
      return this.allChats[this.chatHistUUID] || [];
    },
  },
  watch: {
    userInput() {
      this.adjustHeight();
    },
  },
  mounted() {
    this.adjustVh();
    window.addEventListener('resize', this.adjustVh);
    const img = new Image();
    img.src = require('@/assets/icons/lawra_logo.svg');
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.adjustVh);
  },
  methods: {
    updateHistUUID(selectedHistUUID) {
      this.chatHistUUID = selectedHistUUID;
      // 변경된 uuid가 allchats key에 없으면 추가
      if (!(selectedHistUUID in this.allChats)) {
        this.allChats[selectedHistUUID] = [];
      }
    },
    handleEnter(event) {
      if (this.isDisabled) {
        return;
      }
      if (event.shiftKey) {
        return;
      } else {
        event.preventDefault();
        this.sendMessage();
      }
    },
    adjustHeight() {
      this.$nextTick(() => {
        const element = this.$refs['input-textarea'];
        element.style.height = 'auto';
        element.style.height = `${element.scrollHeight}px`;
      });
    },
    adjustVh() {
      let vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    },
    showRef(caseNum) {
      this.caseRef = this.allCaseRefs[caseNum] || {};
      this.zIndexRef = 3;
      this.$nextTick(() => {
        this.caseWindowVisible = true;
        this.overlayVisible = true;
      });
    },
    closeRef() {
      this.caseWindowVisible = false;
      this.overlayVisible = false;
    },
    showNav() {
      this.zIndexNav = 3;
      this.navVisible = true;
      this.overlayVisible = true;
    },
    closeNav() {
      this.navVisible = false;
      this.overlayVisible = false;
    },
    afterLeaveNav() {
      this.zIndexNav = 1;
    },
    afterLeaveRef() {
      this.zIndexRef = 1;
    },
    summarize(caseNum) {
      if (this.isDisabled) return;
      this.sendMessage(`${caseNum} 판례의 내용을 요약해줘.`);
    },
    scrollBottom(refName) {
      this.$nextTick(() => {
        let ref = this.$refs[refName];
        if (ref) {
          ref.scrollTo({ top: ref.scrollHeight, behavior: 'smooth' });
        }
      });
    },
    async sendMessageMock() {
      let chatArray = this.allChats[this.chatHistUUID] || this.allChats['temp'];

      chatArray.push({
        content: this.userInput,
        isBot: false,
      });
      this.userInput = '';

      setTimeout(() => {
        chatArray.push({
          content: `목업 메시지~~ ${Math.random()}`,
          isBot: true,
        });
      }, 1000);

      // 95다39533 판례 가져오기
      if ('95다39533' in this.allCaseRefs) return;
      try {
        let caseref = await fetch(`${this.baseURL}/api/v1/case_ref`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ case_nums: ['95다39533'] }),
        });
        let caseArray = await caseref.json();
        caseArray = caseArray['case_ref'];
        caseArray.forEach((caseData) => {
          this.allCaseRefs[caseData['사건번호']] = {
            사건번호: caseData['사건번호'],
            법원명: caseData['법원명'],
            선고일자: caseData['선고일자'],
            판례본문: caseData['판례본문'],
            판결요지: caseData['판결요지'],
            참조조문: caseData['참조조문'],
            참조판례: caseData['참조판례'],
          };
        });
      } catch (error) {
        console.error(
          `서버로부터 응답 받아오기 실패 \n ${error.name} \n ${error.message}`
        );
      }
    },
    async sendMessage(message) {
      this.isBegin = false;
      let userInput;
      if (message) {
        userInput = message;
      } else {
        userInput = this.userInput;
      }
      if (!userInput.trim()) return;

      let chatArray = this.allChats[this.chatHistUUID] || this.allChats['temp'];
      chatArray.push({ content: userInput, isBot: false });
      this.userInput = '';
      this.isDisabled = true;
      this.scrollBottom('chatBox');

      setTimeout(() => {
        chatArray.push({
          content: 'lawra가 메시지를 생성중입니다...',
          isBot: true,
          isLoading: true,
        });
        this.scrollBottom('chatBox');
      }, 500);

      try {
        let response = await fetch(`${this.baseURL}/api/v1/query`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ query: userInput }),
        });

        if (!response.ok) {
          throw new Error(`error! status: ${response.status}`);
        }
        let responseData = await response.json();
        let caseArray = responseData.case_ref;
        let caseNums = [];

        // Optional: 판시사항, 판결요지, 참조조문, 참조판례, 주문, 판사명
        caseArray.forEach((caseData) => {
          caseNums.push(caseData['사건번호']);
          this.allCaseRefs[caseData['사건번호']] = caseData;
        });
        let lastChat = chatArray[chatArray.length - 1];
        lastChat.content = responseData.ans;
        lastChat.caseNums = caseNums;
        delete lastChat.isLoading;
        this.scrollBottom('chatBox');
      } catch (error) {
        console.error('서버로부터 응답 받아오기 실패', error);
        chatArray.push({
          content: '오류가 발생했습니다. 다시 시도해주세요.',
          isBot: true,
        });
      }
      this.isDisabled = false;
    },
  },
};
</script>

<style>
* {
  margin: 0;
  padding: 0;
  font-size: 15px;
}
#root-div {
  display: flex;
  position: relative;
  background-color: white;
  width: 100vw;
  height: calc(var(--vh, 1vh) * 100);
  overflow-x: hidden;
  overflow-y: hidden;
}

/* 채팅영역 */
#chat-window {
  width: 45vw;
  min-width: 320px;
  max-width: 768px;
  position: relative;
  flex-shrink: 1;
  flex-grow: 1;
}
#chat-window-header {
  height: 60px;
  display: none;
  background-color: #f3f3ee;
  position: relative;
  justify-content: center;
  align-items: center;
  box-shadow: lightgray 0 5px 10px -10px;
}
#hamburger {
  height: 60%;
  position: absolute;
  border-radius: 50%;
  background-color: transparent;
  cursor: pointer;
  left: 10px;
}
#hamburger:active {
  background-color: #bbbbbb;
}
#chat-header-logo {
  height: 70%;
  margin: 5px;
}
#chat-window-header h2 {
  font-size: 1.5rem;
}
#chat-box {
  width: 95%;
  margin: 10px auto;
  margin-top: 30px;
  max-height: 80%;
  overflow-y: scroll;
}
#input-box {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  width: 90%;
  position: absolute;
  bottom: 2%;
  left: 5%;
  background-color: white;
  border: 1px solid gray;
  border-radius: 5px;
}
#input-textarea {
  flex-grow: 1;
  margin: 5px;
  font-size: 1.2rem;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  resize: none;
  border: none;
}
#input-textarea:focus {
  outline: none;
  border: none;
}
#input-send-button {
  flex-shrink: 0;
  align-self: flex-end;
  background-color: lightgray;
  width: 25px;
  height: 25px;
  margin: 5px;
  cursor: pointer;
  border: none;
  border-radius: 5px;
}
#query-example {
  position: absolute;
  top: -80px;
  width: 100%;
  text-align: center;
}
#query-example p {
  display: inline-block;
  border: 1px solid lightgray;
  color: rgb(59, 59, 59);
  font-size: 0.9rem;
  border-radius: 5px;
  padding: 5px;
  margin: 5px 10px;
  cursor: pointer;
}
#overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.2);
}
.hidden {
  display: none;
}

/* 트랜지션 설정 */
.slide-case-window-enter-active,
.slide-case-window-leave-active,
.slide-nav-enter-active,
.slide-nav-leave-active {
  transition: all 0.5s;
}
.slide-case-window-enter-from,
.slide-case-window-leave-to {
  opacity: 0;
  transform: translateX(20%);
}
.slide-case-window-enter-to,
.slide-case-window-leave-from {
  opacity: 1;
  transform: translateX(0);
}
.slide-nav-enter-from,
.slide-nav-leave-to {
  transform: translateX(-100%);
}
.slide-nav-enter-to,
.slide-nav-leave-from {
  transform: translateX(0);
}
.overlay-enter-active,
.overlay-leave-active {
  transition: all 0.3s;
}
.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}
.overlay-enter-to,
.overlay-leave-from {
  opacity: 1;
}

/* 스크롤바 커스텀 */
::-webkit-scrollbar {
  width: 5px;
}
::-webkit-scrollbar-thumb {
  height: 1%;
  background: grey;
  border-radius: 5px;
}

@media screen and (min-width: 1024px) {
  #overlay {
    /* 데스크탑 환경에선 오버레이 표시 x */
    display: none !important;
  }
}
@media screen and (max-width: 767.5px) {
  #chat-window-header {
    /* 모바일 환경에서 챗-헤더 표시 x */
    display: flex;
  }
}
</style>
